/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Load the backend-authoritative active role context for the Supabase identity.
 * @state-owner TanStack Query
 * @allowed-imports app environment/session; contracts; TanStack Query
 * @forbidden localStorage; Zustand; permission inference; generated SDK direct imports
 * @public-api auth module
 * @side-effects authenticated account-role request
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests module-contracts.test.ts
 * @security-boundary authenticated FastAPI context response
 * @data-classification membership role and account scope
 * @trust-model backend current_role is authoritative; demo mode is explicitly non-production
 */

import { useQuery } from '@tanstack/react-query';
import { type ActiveContext, accountRolesResponseSchema, roleSchema } from '@vispo/contracts';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';

export const authKeys = {
  all: ['auth'] as const,
  activeContext: (userId: string) => [...authKeys.all, 'active-context', userId] as const,
};

function mapActiveContext(value: unknown, userId: string): ActiveContext | null {
  const response = accountRolesResponseSchema.parse(value);
  const roleResult = roleSchema.safeParse(response.current_role);
  if (!roleResult.success) return null;
  return {
    id: `${userId}:${roleResult.data}`,
    organizationId: userId,
    classId: null,
    role: roleResult.data,
  };
}

export function useActiveContext() {
  const environment = useWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';

  return useQuery({
    queryKey: authKeys.activeContext(userId),
    enabled: Boolean(session),
    queryFn: async (): Promise<ActiveContext | null> => {
      if (!session || environment.demoMode) return null;
      const response = await environment.apiClient.request<unknown>('/api/auth/active-context');
      return mapActiveContext(response, session.userId);
    },
    staleTime: 30_000,
  });
}
